import React, { useState } from "react";
import clsx from "clsx";
import addDays from "date-fns/addDays";
import format from "date-fns/format";
import isToday from "date-fns/isToday";
import isTomorrow from "date-fns/isTomorrow";
import parseISO from "date-fns/parseISO";

import MenuItem from "@material-ui/core/MenuItem";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import Divider from "@material-ui/core/Divider";
import Typography from "@material-ui/core/Typography";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import CancelIcon from "components/Icons/Cancel";
import { shouldShutOffDate } from "utils/date-utils";
import useStyles from "./confirmationStyles";

export interface Props {
  horse: racehorse360.IHorse;
  workoutData: racehorse360.IWorkout;
  onConfirm: () => void;
}

const CancelConfirmation = (props: Props) => {
  const { horse, workoutData, onConfirm } = props;
  const classes = useStyles();

  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState(false);

  const handleCancelWorkoutClick = event => {
    event.stopPropagation();
    const isShutOffDate = shouldShutOffDate(
      parseISO(workoutData.date),
      workoutData?.facility
    );

    setOpen(isShutOffDate);
    !isShutOffDate && onConfirm();
  };

  const handleConfirmationClose = event => {
    event.stopPropagation();
    setOpen(false);
  };

  const handleCancelConfirmClick = event => {
    event.stopPropagation();
    setOpen(false);
    setConfirm(true);
  };

  const handleExited = () => {
    if (confirm) {
      setConfirm(false);
      onConfirm();
    }
  };

  return (
    <>
      <MenuItem onClick={handleCancelWorkoutClick}>
        <ListItemIcon>
          <CancelIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="Cancel Workout" />
      </MenuItem>
      <Dialog
        closeAfterTransition
        transitionDuration={300}
        open={open}
        TransitionProps={{
          onExited: handleExited
        }}
        onClose={handleConfirmationClose}
        className={classes.confirmationDialog}
      >
        {workoutData && workoutData.facility && (
          <>
            <DialogTitle>
              <Typography className={classes.dialogTitleTypography}>
                Cancel Workout?
              </Typography>
            </DialogTitle>
            <DialogTitle className={classes.dialogSubtitle}>
              <Typography
                color={"textSecondary"}
                className={classes.dialogSubtitleTypography}
              >
                You are about to cancel the following workout for&nbsp;
                <Typography
                  component="b"
                  className={clsx(
                    classes.dialogSubtitleTypography,
                    classes.dialogBold
                  )}
                >
                  {isToday(parseISO(workoutData.date)) && "today"}
                  {isTomorrow(parseISO(workoutData?.date)) && "tomorrow"}:
                </Typography>
              </Typography>
            </DialogTitle>
            <Divider />
            <DialogContent>
              <Typography
                color={"textSecondary"}
                className={classes.dialogHorseInfo}
              >
                <Typography
                  component="b"
                  className={clsx(classes.dialogHorseInfo, classes.dialogBold)}
                >
                  Horse:&nbsp;
                </Typography>
                {horse.name}
              </Typography>
              <Typography
                color={"textSecondary"}
                className={classes.dialogHorseInfo}
              >
                <Typography
                  component="b"
                  className={clsx(classes.dialogHorseInfo, classes.dialogBold)}
                >
                  Track:&nbsp;
                </Typography>
                {workoutData?.facility?.name}
              </Typography>
              <Typography
                color={"textSecondary"}
                className={classes.dialogHorseInfo}
              >
                <Typography
                  component="b"
                  className={clsx(classes.dialogHorseInfo, classes.dialogBold)}
                >
                  Workout Date:&nbsp;
                </Typography>
                {format(parseISO(workoutData?.date), "iiii, MMMM d")}
                <Typography
                  component="b"
                  className={clsx(classes.dialogHorseInfo, classes.dialogBold)}
                >
                  {isToday(parseISO(workoutData?.date)) && " (Today)"}
                </Typography>
              </Typography>
            </DialogContent>
            <Divider light />
            <DialogContent>
              <Typography
                color={"textSecondary"}
                className={classes.dialogSubtitleTypography}
              >
                All workouts must be scheduled two days in advance. If you
                choose to cancel this workout, the next available workout date
                will be{" "}
                <Typography
                  component="b"
                  className={clsx(
                    classes.dialogSubtitleTypography,
                    classes.dialogBold
                  )}
                >
                  {format(addDays(new Date(), 2), "iiii, MMMM d")}
                </Typography>
                .
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button
                className={classes.button}
                onClick={handleConfirmationClose}
                color="primary"
              >
                Close
              </Button>
              <Button
                className={clsx(classes.button, classes.confirmButton)}
                onClick={handleCancelConfirmClick}
              >
                Cancel Workout
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
};

export default CancelConfirmation;
